import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { History } from 'history'

import { fetchRefiTransactions } from './refiTransactionsActions'
import {
  selectRefiTransactionIndex,
  selectRefiTransactionMeta,
} from '../../reducers/refiTransactions'
import {
  stringifyQuery,
  parseQuery,
  toggleSort,
} from '../../helpers/queryHelper'
import {
  previousPage,
  goToPage,
  nextPage,
} from '../../helpers/paginationHelper'
import { utcToLocalDate } from '../../helpers/timeHelper'
import FilterBadge from '../../components/filterBadge'
import IndexHeaderRow from '../common/indexes/indexHeaderRow'

import { RefiTransaction } from './types'

import Breadcrumb from '../../components/Breadcrumb'
import TablePagination from '../../components/tablePagination'

export type Callback<T = void> = (...args: any) => T

export interface Props {
  fetchRefiTransactions: Callback
  currentRefiTransactions: RefiTransaction[]
  history: History
  meta: { total: number }
  location: { key: any } & Location // TODO: why?
}

export class RefiTransactionsIndex extends Component<Props> {
  static displayName = 'RefiTransactionsIndex'

  componentWillMount() {
    const { fetchRefiTransactions, location, history } = this.props
    if (!location.search) {
      history.push({
        pathname: './refinance',
        search: stringifyQuery({
          offset: 0,
          limit: 25,
        }),
      })
    } else {
      const parsedQuery = parseQuery(location.search)
      fetchRefiTransactions(parsedQuery)
    }
  }

  componentDidUpdate(prevProps) {
    const {
      fetchRefiTransactions,
      location: { search },
    } = this.props
    if (search !== prevProps.location.search) {
      const parsedQuery = parseQuery(search)
      fetchRefiTransactions(parsedQuery)
    }
  }

  prevPageClick = () => {
    const {
      location: { search },
    } = this.props
    previousPage(search, this.props.history, '/refinance')
  }

  nextPageClick = () => {
    const {
      currentRefiTransactions,
      location: { search },
    } = this.props
    nextPage(
      search,
      this.props.history,
      currentRefiTransactions.length,
      '/refinance'
    )
  }

  onPageClick = (pageNum) => {
    const {
      location: { search },
    } = this.props
    goToPage(search, this.props.history, pageNum, '/refinance')
  }

  filtersClick = () => {
    const {
      location: { search },
    } = this.props
    this.props.history.push({
      pathname: '/refinance/filters',
      search,
    })
  }

  headerClick = (sortEnum) => {
    const {
      location: { search },
    } = this.props
    this.props.history.push({
      pathname: '/refinance',
      search: toggleSort(search, sortEnum, 'DESC'),
    })
  }

  openTxn = (refinanceTransactionId) => {
    window.location.href = `/refinance/${refinanceTransactionId}`
  }

  render() {
    const { currentRefiTransactions, meta, location } = this.props
    const { total } = meta
    const { search } = location
    const parsedSearch = parseQuery(search)
    const totalPages = Math.ceil(total / parsedSearch.limit)
    const headerList = [
      { displayHeader: 'NAME', sortable: false },
      { displayHeader: 'STATE', sortable: false },
      { displayHeader: 'REFINANCE TIER', sortable: false },
      { displayHeader: 'STATUS', sortable: false },
      {
        displayHeader: 'DECISION DATE',
        sortable: true,
        sortEnum: 'LAST_DECISION_AT',
      },
      {
        displayHeader: 'TRADELINE RATE',
        sortable: true,
        sortEnum: 'CURRENT_LOAN_APR',
      },
      { displayHeader: 'LOAN RATE', sortable: true, sortEnum: 'LOAN_RATE' },
      {
        displayHeader: 'APR IMPROVEMENT',
        sortable: true,
        sortEnum: 'APR_IMPROVEMENT',
      },
      { displayHeader: 'LOAN ID', sortable: false },
      { displayHeader: 'EXT ID', sortable: false },
    ]

    const levels = [
      { label: 'Index ', path: '/' },
      { label: 'Refinance', path: '/refinance' },
    ]

    const rows = currentRefiTransactions.map((refiTransaction, index) => {
      const { id, user, state, currentLoan, loan, status, aprImprovement } =
        refiTransaction
      return (
        <tr
          className="index-table__row"
          key={index}
          onClick={() => this.openTxn(id)}
        >
          {user ? (
            <td className="index-table__cell">{`${user.lastName}, ${user.firstName}`}</td>
          ) : (
            <td />
          )}
          {state ? <td className="index-table__cell">{state}</td> : <td />}
          {loan && loan.tier ? (
            <td className="index-table__cell">{loan.tier}</td>
          ) : (
            <td />
          )}
          {status ? <td className="index-table__cell">{status}</td> : <td />}
          {loan && loan.lastDecisionAt ? (
            <td className="index-table__cell">
              {utcToLocalDate(loan.lastDecisionAt)}
            </td>
          ) : (
            <td />
          )}
          {currentLoan && currentLoan.apr ? (
            <td className="index-table__cell">{currentLoan.apr}%</td>
          ) : (
            <td />
          )}
          {loan && loan.interestRate ? (
            <td className="index-table__cell">{loan.interestRate}%</td>
          ) : (
            <td />
          )}
          {aprImprovement ? (
            <td className="index-table__cell">{aprImprovement}</td>
          ) : (
            <td />
          )}
          <td className="index-table__cell">{loan && loan.id}</td>
          <td className="index-table__cell">{loan && loan.externalId}</td>
        </tr>
      )
    })

    return (
      <>
        <Breadcrumb levels={levels} />
        <div className="index index__drive-america" key={location.key}>
          <h1 className="index-table__header">
            Refinance Queue
            <span className="index-table__header--total">{total}</span>
          </h1>
          <div className="index-table__drive-america">
            <a
              data-testid="FiltersLink"
              onClick={this.filtersClick}
              className="index-table__filter-button"
            >
              <FilterBadge renderProps={this.props} />
              filters
            </a>
            <a
              href="/refiapp"
              className="button button-drive-america link-drive-america link-drive-america__new-applicant"
            >
              New Customer
            </a>
          </div>

          <table className="index-table">
            <IndexHeaderRow
              headerList={headerList}
              parsedSearch={parsedSearch}
              clickCallback={this.headerClick}
            />
            <tbody className="index-table__body">{rows}</tbody>
          </table>
          <TablePagination
            search={location.search}
            queryLength={currentRefiTransactions.length}
            totalPages={totalPages}
            prevPageClick={this.prevPageClick}
            onPageClick={this.onPageClick}
            nextPageClick={this.nextPageClick}
          />
        </div>
      </>
    )
  }
}

export const mapStateToProps = (state) => {
  return {
    currentRefiTransactions: selectRefiTransactionIndex(state.refiTransactions),
    meta: selectRefiTransactionMeta(state.refiTransactions),
  }
}

export default withRouter(
  connect(mapStateToProps, {
    fetchRefiTransactions,
  })(RefiTransactionsIndex)
)
